import { useState, useEffect, useRef } from 'react';
import '../styles/main.css';
import { BrowserMultiFormatReader } from '@zxing/library';

export default function BarcodeScanner({ onScan, onClose, useCamera = false }) {
  const [barcode, setBarcode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState('');
  const [useCameraMode, setUseCameraMode] = useState(useCamera); // Fixed: renamed to avoid conflict with prop
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const codeReader = useRef(null);

  // Handle manual barcode input
  const handleSubmit = (e) => {
    e.preventDefault();
    if (barcode.trim()) {
      onScan(barcode.trim());
      setBarcode('');
    }
  };

  // Initialize camera scanner
  const startCamera = async () => {
    try {
      setCameraActive(true);
      setScanning(true);
      
      codeReader.current = new BrowserMultiFormatReader();
      
      const videoInputDevices = await codeReader.current.listVideoInputDevices();
      const selectedDeviceId = videoInputDevices[0]?.deviceId;
      
      if (selectedDeviceId) {
        await codeReader.current.decodeFromVideoDevice(
          selectedDeviceId,
          videoRef.current,
          (result, error) => {
            if (result) {
              const scannedCode = result.getText();
              onScan(scannedCode);
              stopCamera();
            }
            if (error && !(error instanceof TypeError)) {
              console.error(error);
            }
          }
        );
      } else {
        setError('No camera found');
        setCameraActive(false);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Failed to access camera');
      setCameraActive(false);
    } finally {
      setScanning(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (codeReader.current) {
      codeReader.current.reset();
      codeReader.current = null;
    }
    setCameraActive(false);
    setScanning(false);
  };

  // Switch to manual mode
  const switchToManual = () => {
    stopCamera();
    setUseCameraMode(false);
  };

  // Switch to camera mode
  const switchToCamera = () => {
    setUseCameraMode(true);
    startCamera();
  };

  // Cleanup on unmount
  useEffect(() => {
    if (useCameraMode) {
      startCamera();
    }
    
    return () => {
      if (codeReader.current) {
        codeReader.current.reset();
      }
    };
  }, [useCameraMode]);

  // Focus input on mount for manual mode
  useEffect(() => {
    if (!useCameraMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [useCameraMode]);

  return (
    <div className="scanner-modal">
      <div className="scanner-content">
        <div className="scanner-header">
          <h3>{useCameraMode ? 'Scan Barcode with Camera' : 'Enter Barcode'}</h3>
          <button className="close-btn" onClick={() => {
            if (cameraActive) stopCamera();
            onClose();
          }}>✕</button>
        </div>

        <div className="scanner-body">
          {useCameraMode ? (
            <>
              <div className="camera-container">
                <video 
                  ref={videoRef} 
                  style={{ width: '100%', height: 'auto' }}
                  className="camera-preview"
                />
                {error && (
                  <div className="camera-error">
                    <p>❌ {error}</p>
                    <button onClick={startCamera} className="btn-retry">
                      Retry
                    </button>
                  </div>
                )}
                {!cameraActive && !error && (
                  <div className="camera-loading">
                    <div className="spinner"></div>
                    <p>Starting camera...</p>
                  </div>
                )}
              </div>
              
              <div className="scanner-info">
                <p>📷 Position barcode in front of camera</p>
                <p className="info-note">Make sure the barcode is clearly visible</p>
              </div>
              
              <div className="scanner-footer">
                <button onClick={stopCamera} className="btn-secondary">
                  Stop Camera
                </button>
                <button onClick={switchToManual} className="btn-link">
                  Enter manually
                </button>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit}>
                <div className="barcode-input-group">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Enter barcode number..."
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    autoFocus
                  />
                  <button type="submit" className="scan-submit">
                    ✓
                  </button>
                </div>
              </form>

              <div className="scanner-info">
                <p>🔢 Enter barcode manually</p>
                <p className="info-note">Or use camera scanner below</p>
              </div>

              <div className="scanner-footer">
                <button onClick={switchToCamera} className="btn-primary">
                  📷 Use Camera
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}